/**
 * Step 2: Data Ingestion Component
 * Ingests ERC-8004 agent data into the provisioned database with X-402 payment
 */

import { useState } from 'react';
import { useAccount, useSignTypedData, useSwitchChain } from 'wagmi';
import { Download, Loader2, CheckCircle, AlertCircle, Database, TrendingUp } from 'lucide-react';
import type { TenantCredentials, IngestionStats, IngestDataRequest } from '@/types/database';

// USDC Contract addresses per network (for EIP-3409 TransferWithAuthorization)
const USDC_ADDRESSES: Record<number, string> = {
  8453: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',   // Base mainnet
  84532: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',  // Base Sepolia testnet
  1: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',     // Ethereum mainnet
  11155111: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238', // Sepolia testnet
};

const INGESTION_COST_USDC = 0.5; // $0.5 USDC for data ingestion

interface IngestDataStepProps {
  credentials: TenantCredentials;
  onComplete: (stats: IngestionStats) => void;
}

export function IngestDataStep({ credentials, onComplete }: IngestDataStepProps) {
  const { address, isConnected, chain } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const { switchChainAsync } = useSwitchChain();

  const [isIngesting, setIsIngesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ingestionStats, setIngestionStats] = useState<IngestionStats | null>(null);
  const [progress, setProgress] = useState<{
    status: string;
    currentCount?: number;
    totalCount?: number;
  }>({ status: 'Ready to ingest data' });

  const handleIngest = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet first');
      return;
    }

    setIsIngesting(true);
    setError(null);
    setProgress({ status: 'Preparing payment...' });

    try {
      const agentServiceUrl = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:5000';

      // Step 1: Request payment requirement from agent
      setProgress({ status: 'Requesting payment details...' });
      const prepareResponse = await fetch(`${agentServiceUrl}/api/v1/kg/ingest-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: {
            messageId: `msg-${Date.now()}`,
            role: 'user',
            parts: [{ kind: 'text', text: `Ingest ERC-8004 data for tenant ${credentials.tenant_id}` }],
            metadata: {
              tenant_id: credentials.tenant_id,
              api_key: credentials.api_key
            }
          }
        })
      });

      if (!prepareResponse.ok) {
        throw new Error(`Failed to prepare payment: ${prepareResponse.status}`);
      }

      const prepareData = await prepareResponse.json();
      const paymentRequest = prepareData.message?.metadata?.['x402.payment.required']?.accepts?.[0];

      if (!paymentRequest) {
        throw new Error('Agent did not return payment requirements');
      }

      // Normalize x402 payment fields (payTo → to, maxAmountRequired → amount)
      const normalizedPayment = {
        to: paymentRequest.payTo,
        amount: paymentRequest.maxAmountRequired,
        network: paymentRequest.network,
        asset: paymentRequest.asset,
        extra: paymentRequest.extra
      };

      // Map network name to chain ID (matching working reference)
      const networkToChainId: Record<string, number> = {
        'base': 8453,
        'baseSepolia': 84532,
        'base-sepolia': 84532,
        'ethereum': 1,
        'sepolia': 11155111,
        'arbitrum': 42161,
        'polygon': 137,
      };

      const requiredChainId = networkToChainId[normalizedPayment.network] || 84532;
      console.log('[Ingest Payment] Network:', normalizedPayment.network, '→ Chain ID:', requiredChainId);

      // Switch to required chain if needed
      if (chain?.id !== requiredChainId) {
        setProgress({ status: `Switching to ${normalizedPayment.network}...` });
        console.log(`[Ingest Payment] Switching from chain ${chain?.id} to ${requiredChainId}...`);
        try {
          await switchChainAsync({ chainId: requiredChainId });
          console.log(`[Ingest Payment] Switched to chain ${requiredChainId} successfully`);
        } catch (switchErr: any) {
          throw new Error(`Failed to switch network. Please manually switch to the required network in your wallet and try again.`);
        }
      }

      // Step 2: Create EIP-3409 TransferWithAuthorization signature
      setProgress({ status: 'Requesting wallet signature...' });
      const usdcAddress = USDC_ADDRESSES[requiredChainId];
      if (!usdcAddress) {
        throw new Error(`USDC contract not configured for chain ${requiredChainId}`);
      }

      const usdcName = normalizedPayment.extra?.name || 'USD Coin';
      const usdcVersion = normalizedPayment.extra?.version || '2';

      // Generate cryptographically secure nonce (32 bytes)
      const nonceBytes = new Uint8Array(32);
      crypto.getRandomValues(nonceBytes);
      const authNonce = '0x' + Array.from(nonceBytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Calculate validBefore (10 minutes from now)
      const validBefore = Math.floor(Date.now() / 1000) + 600;

      // Create authorization object for EIP-3409
      const authorization = {
        from: address,
        to: normalizedPayment.to,
        value: normalizedPayment.amount,
        validAfter: '0',
        validBefore: validBefore.toString(),
        nonce: authNonce
      };

      console.log('[Ingest Payment] Signing EIP-3409 TransferWithAuthorization...');
      console.log('[Ingest Payment] USDC contract:', usdcAddress);
      console.log('[Ingest Payment] USDC domain:', { name: usdcName, version: usdcVersion, chainId: requiredChainId });
      console.log('[Ingest Payment] Authorization:', authorization);

      // Sign against USDC contract's EIP-712 domain (NOT custom x402 domain!)
      const signature = await signTypedDataAsync({
        domain: {
          name: usdcName,
          version: usdcVersion,
          chainId: requiredChainId,
          verifyingContract: usdcAddress as `0x${string}`
        },
        types: {
          TransferWithAuthorization: [
            { name: 'from', type: 'address' },
            { name: 'to', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'validAfter', type: 'uint256' },
            { name: 'validBefore', type: 'uint256' },
            { name: 'nonce', type: 'bytes32' }
          ]
        },
        primaryType: 'TransferWithAuthorization',
        message: {
          from: address as `0x${string}`,
          to: normalizedPayment.to as `0x${string}`,
          value: BigInt(normalizedPayment.amount),
          validAfter: BigInt(0),
          validBefore: BigInt(validBefore),
          nonce: authNonce as `0x${string}`
        }
      });

      console.log('[Ingest Payment] EIP-3409 signature obtained:', signature);

      // Step 3: Submit with payment to agent endpoint
      setProgress({ status: 'Starting data ingestion...', currentCount: 0, totalCount: 6597 });

      // Payment payload (nested format for our backend)
      const paymentPayload = {
        x402Version: 1,
        scheme: 'exact',
        network: normalizedPayment.network,
        asset: usdcAddress,
        payload: {
          signature,
          authorization
        }
      };

      const response = await fetch(`${agentServiceUrl}/api/v1/kg/ingest-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: {
            messageId: `msg-${Date.now()}`,
            role: 'user',
            parts: [{ kind: 'text', text: `Ingest ERC-8004 data for tenant ${credentials.tenant_id}` }],
            metadata: {
              tenant_id: credentials.tenant_id,
              api_key: credentials.api_key,
              'x402.payment.payload': paymentPayload,
              'x402.payment.status': 'payment-submitted'
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Ingestion failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Ingestion failed');
      }

      // Store ingestion stats
      const stats: IngestionStats = result.data;
      setIngestionStats(stats);
      setProgress({
        status: 'Ingestion complete!',
        currentCount: stats.agents_synced,
        totalCount: stats.agents_synced
      });

      // Notify parent
      onComplete(stats);

    } catch (err: any) {
      console.error('[Ingestion Error]', err);

      // User-friendly error messages (matching working reference)
      let userMessage = 'Failed to ingest data';

      if (err instanceof Error) {
        const errMsg = err.message.toLowerCase();

        if (errMsg.includes('user rejected') || errMsg.includes('user denied')) {
          userMessage = 'You rejected the wallet signature. Please try again and approve the transaction.';
        } else if (errMsg.includes('insufficient funds') || errMsg.includes('insufficient balance')) {
          userMessage = `Insufficient USDC balance. Please add at least $${INGESTION_COST_USDC} USDC to your wallet on the required network.`;
        } else if (errMsg.includes('network') || errMsg.includes('switch')) {
          userMessage = err.message; // Already user-friendly from switch error
        } else if (errMsg.includes('payment') || errMsg.includes('402')) {
          userMessage = 'Payment verification failed. Please ensure you have sufficient USDC balance and the correct network.';
        } else if (errMsg.includes('timeout') || errMsg.includes('timed out')) {
          userMessage = 'Request timed out. The ingestion service might be busy, please try again in a moment.';
        } else {
          userMessage = `Ingestion failed: ${err.message}`;
        }
      }

      setError(userMessage);
      setProgress({ status: 'Ingestion failed' });
    } finally {
      setIsIngesting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Download className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Ingest ERC-8004 Agents</h2>
            <p className="text-gray-600 mt-1">Load agent data from the ERC-8004 subgraph into your database</p>
          </div>
        </div>

        {!ingestionStats ? (
          <>
            {/* Tenant Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Database Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Tenant ID</p>
                  <p className="font-mono text-gray-900">{credentials.tenant_id.substring(0, 16)}...</p>
                </div>
                <div>
                  <p className="text-gray-600">Keyspace</p>
                  <p className="font-mono text-gray-900">{credentials.keyspace_name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Tier</p>
                  <p className="font-semibold text-gray-900 capitalize">{credentials.tier}</p>
                </div>
                <div>
                  <p className="text-gray-600">Quota</p>
                  <p className="font-semibold text-gray-900">{credentials.quota.max_nodes.toLocaleString()} nodes</p>
                </div>
              </div>
            </div>

            {/* Ingestion Details */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">What will be ingested?</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>~6,597 ERC-8004 agents from multiple chains</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>381 feedback entries with quality scores</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>467 validation records</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Complete agent metadata (skills, capabilities, endpoints)</span>
                </div>
              </div>
            </div>

            {/* Cost Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Database className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Ingestion Cost</p>
                  <p className="text-sm text-blue-700 mt-1">
                    This operation costs <strong>${INGESTION_COST_USDC} USDC</strong> and will populate your database with the complete ERC-8004 agent knowledge graph.
                  </p>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Ingestion Failed</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Progress Display */}
            {isIngesting && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                  <p className="text-sm font-medium text-purple-900">{progress.status}</p>
                </div>
                {progress.totalCount && (
                  <div className="w-full bg-purple-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${((progress.currentCount || 0) / progress.totalCount) * 100}%`
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Ingest Button */}
            <button
              onClick={handleIngest}
              disabled={isIngesting || !isConnected}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
            >
              {isIngesting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Ingesting Data...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Ingest Data (${INGESTION_COST_USDC} USDC)
                </>
              )}
            </button>
          </>
        ) : (
          /* Success State - Display Stats */
          <div className="space-y-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Data Ingestion Complete!</p>
                <p className="text-sm text-green-700 mt-1">
                  Successfully loaded {ingestionStats.agents_synced.toLocaleString()} agents into your database in {ingestionStats.duration_sec}s.
                </p>
              </div>
            </div>

            {/* Ingestion Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  <p className="text-sm font-medium text-gray-600">Agents Synced</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">{ingestionStats.agents_synced.toLocaleString()}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-medium text-gray-600">Nodes Created</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">{ingestionStats.nodes_created.toLocaleString()}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <p className="text-sm font-medium text-gray-600">Edges Created</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">{ingestionStats.edges_created.toLocaleString()}</p>
              </div>
            </div>

            {/* TEE Proof (if available) */}
            {ingestionStats.tee_proof && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">🔒 TEE Attestation</p>
                <p className="text-xs font-mono text-blue-700 break-all">
                  {ingestionStats.tee_proof.signature.substring(0, 64)}...
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Verified at {new Date(ingestionStats.tee_proof.timestamp).toLocaleString()}
                </p>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => onComplete(ingestionStats)}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Continue to Agent Explorer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
